<template>
    <div class="table-responsive calendar-grid-v2">
        <table class="table table-bordered table-sm">
            <thead>
            <tr>
                <th class="calendar-day-col">Giorno</th>
                <th
                    v-for="h in hours"
                    :key="h"
                    class="calendar-hour-col"
                    :class="getHourHeaderClass(h)"
                >
                    <span class="hour-start">{{ h }}</span>
                    <span class="hour-arrow">→</span>
                    <span class="hour-end">{{ addOneHour(h) }}</span>
                    <small v-if="mixedMinutePhases" class="hour-phase-label">fase :{{ minutePhase(h) }}</small>
                </th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="day in normalizedDays" :key="day.date">
                <td
                    class="calendar-day-col"
                    :class="{ 'calendar-day-col-actionable': mode === 'modifica' }"
                    :title="mode === 'modifica' ? 'Azioni giornata' : ''"
                    @click="onDayClick(day)"
                >
                    <div class="font-weight-bold">{{ day.label || day.date }}</div>
                    <small v-if="day.weekday">{{ weekdayLabel(day.weekday) }}</small>
                    <button
                        v-if="mode === 'modifica'"
                        type="button"
                        class="day-action-button"
                        @click.stop="onDayClick(day)"
                    >
                        Azioni
                    </button>
                </td>

                <td
                    v-for="h in hours"
                    :key="day.date + '_' + h"
                    :class="getCellClass(day.date, h)"
                    :title="getCellTitle(day.date, h)"
                    @click="onClick(day.date, h)"
                    @contextmenu.prevent.stop="onContextMenu(day.date, h)"
                >
                    <div
                        v-for="slot in cellsFor(day.date, h)"
                        :key="(slot.giornata_id || slot.id) + '_' + h + '_' + slot.corso_id"
                        class="calendar-slot-content calendar-slot-lane"
                        :class="{ 'calendar-slot-locked': isDateLocked(day.date, slot.corso_id) }"
                        :style="{ borderLeftColor: slot.course_color || '#2563eb' }"
                        @click.stop="onSlotClick(day.date, h, slot)"
                    >
                        <span v-if="slot.course_label" class="slot-course">{{ slot.course_label }}</span>
                        <span
                            v-if="getDeclaredWarning(day.date, h, slot)"
                            class="declared-availability-warning"
                            :title="getDeclaredWarning(day.date, h, slot).message"
                        >!</span>
                        <span
                            v-if="slot.outside_course_hours_exception"
                            class="course-hours-exception-badge"
                            :title="'Eccezione fuori orario: ' + (slot.calendar_exception_reason || '')"
                        >E</span>
                        <div class="slot-module">
                            <span class="slot-module-badge">
                                {{ getModuloShort(slot) }}
                            </span>
                            <span v-if="getModuloDetail(slot)" class="slot-module-detail">
                                · {{ getModuloDetail(slot) }}
                            </span>
                        </div>
                        <div class="slot-teacher">
                            {{ slot.docente_nome }}
                        </div>
                        <div v-if="slot.tutor_nome" class="slot-extra">
                            Tutor: {{ slot.tutor_nome }}
                        </div>
                        <div v-if="slot.codocente_nome" class="slot-extra">
                            Co-docente: {{ slot.codocente_nome }}
                        </div>
                        <div v-if="mode === 'modifica'" class="slot-actions">
                            <button
                                v-if="getDeclaredWarning(day.date, h, slot)"
                                type="button"
                                class="slot-action-btn slot-action-request"
                                title="Invia al docente una richiesta di conferma"
                                @click.stop="requestAvailabilityConfirmation(day.date, h, slot)"
                            >
                                Richiedi conferma
                            </button>
                            <button
                                type="button"
                                class="slot-action-btn slot-action-edit"
                                title="Modifica tutor o co-docente"
                                @click.stop="onContextMenu(day.date, h, slot)"
                            >
                                Modifica
                            </button>
                            <span class="slot-action-btn slot-action-delete">
                                Elimina
                            </span>
                        </div>
                    </div>

                    <div v-if="!cellsFor(day.date, h).length" class="calendar-empty-content">
                        <span v-if="mode === 'modifica'">{{ isDateLocked(day.date, activeCourseId) ? '🔒' : (isActivePhase(h) ? '+' : '·') }}</span>
                        <small
                            v-if="mode === 'modifica' && getAvailability(day.date, h) && getAvailability(day.date, h).available === false"
                            class="availability-hint"
                        >
                            {{ getAvailabilityShortLabel(day.date, h) }}
                            <button type="button" class="btn btn-xs btn-default" @click.stop="$emit('teacher-schedule', {date: day.date, availability: getAvailability(day.date, h)})">Consulta impegni</button>
                        </small>
                        <small
                            v-else-if="mode === 'modifica' && getDeclaredWarning(day.date, h)"
                            class="availability-hint"
                        >! Fuori disponibilità</small>
                    </div>
                    <button
                        v-else-if="mode === 'modifica'"
                        type="button"
                        class="parallel-add-button"
                        :disabled="isDateLocked(day.date, activeCourseId) || !isActivePhase(h)"
                        title="Aggiungi una pianificazione parallela per il corso del modulo selezionato"
                        @click.stop="onClick(day.date, h)"
                    >+ aggiungi</button>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    props: {
        days: {
            type: Array,
            default: () => []
        },
        hours: {
            type: Array,
            default: () => []
        },
        slots: {
            type: Array,
            default: () => []
        },
        availability: {
            type: Object,
            default: () => ({})
        },
        mode: {
            type: String,
            default: 'consultazione'
        },
        selection: {
            type: Object,
            default: () => ({})
        },
        activeCourseId: {
            type: [Number, String],
            default: null
        },
        activeMinutePhase: {
            type: String,
            default: '00'
        },
        mixedMinutePhases: {
            type: Boolean,
            default: false
        },
        lockedRanges: {
            type: Array,
            default: () => []
        }
    },

    computed: {
        normalizedDays() {
            return this.days || []
        },

        slotsByKey() {
            const map = {}

            ;(this.slots || []).forEach((slot) => {
                const key = this.makeSlotKey(slot.date || slot.day, slot.hour || slot.time || slot.start, slot.aula_id || 0)
                if (!map[key]) map[key] = []
                map[key].push(slot)
            })

            return map
        }
    },

    methods: {
        cellFor(date, hour) {
            return this.getCell(date, hour)
        },

        cellsFor(date, hour) {
            const selectedAulaId = this.selection && this.selection.aula_id ? this.selection.aula_id : 0
            return (this.slots || []).filter((slot) => {
                const sameCell = (slot.date || slot.day) === date && (slot.hour || slot.time || slot.start) === hour
                return sameCell && (!selectedAulaId || Number(slot.aula_id || 0) === Number(selectedAulaId))
            })
        },

        getCell(date, hour) {
            return this.cellsFor(date, hour)[0] || null
        },

        isDateLocked(date, courseId) {
            return !!this.getDateLock(date, courseId)
        },

        getDateLock(date, courseId) {
            return (this.lockedRanges || []).find((range) => {
                return Number(range.course_id) === Number(courseId) && date >= range.date_from && date <= range.date_to
            }) || null
        },

        getCellClass(date, hour) {
            const cells = this.cellsFor(date, hour)
            const availability = this.getAvailability(date, hour)

            return {
                'calendar-cell': true,
                'calendar-cell-empty': !cells.length,
                'calendar-cell-occupied': !!cells.length,
                'calendar-cell-locked': this.isDateLocked(date, this.activeCourseId),
                'calendar-cell-busy': !cells.length && availability && availability.busy,
                'calendar-cell-unavailable': !cells.length && availability && availability.available === false,
                'calendar-cell-declared-warning': !!this.getDeclaredWarning(date, hour),
                'calendar-cell-other-phase': this.mixedMinutePhases && !this.isActivePhase(hour),
                'cursor-pointer': this.mode === 'modifica' && this.isActivePhase(hour)
            }
        },

        getHourHeaderClass(hour) {
            return {
                'calendar-hour-phase-00': this.mixedMinutePhases && this.minutePhase(hour) === '00',
                'calendar-hour-phase-30': this.mixedMinutePhases && this.minutePhase(hour) === '30',
                'calendar-hour-active-phase': this.mixedMinutePhases && this.isActivePhase(hour)
            }
        },

        minutePhase(hour) {
            return String(hour || '').slice(3, 5) === '30' ? '30' : '00'
        },

        isActivePhase(hour) {
            return this.minutePhase(hour) === (String(this.activeMinutePhase) === '30' ? '30' : '00')
        },

        getCellTitle(date, hour) {
            const slot = this.getCell(date, hour)
            const availability = this.getAvailability(date, hour)

            if (this.mode !== 'modifica') {
                if (slot) {
                    return 'Pianificazione: ' + this.getModuloTitle(slot) + ' - ' + (slot.docente_nome || '')
                }
                return ''
            }

            const lock = this.getDateLock(date, slot && slot.corso_id ? slot.corso_id : this.activeCourseId)
            if (lock) {
                return lock.lock_type === 'ddr'
                    ? (lock.label || 'DDR attiva') + ': giornata disponibile in sola lettura'
                    : 'Calendario in verifica: giornata disponibile in sola lettura'
            }

            if (!slot && this.mixedMinutePhases && !this.isActivePhase(hour)) {
                return 'Fase :' + this.minutePhase(hour) + ' visibile per gli altri corsi. Il corso attivo usa la fase :' + this.activeMinutePhase + '.'
            }

            if (slot) {
                const warning = this.getDeclaredWarning(date, hour)
                const action = 'Elimina pianificazione ' + (slot.start || hour) + ' - ' + (slot.end || this.addOneHour(hour))
                return warning ? warning.message + ' — ' + action : action
            }

            if (availability && availability.available === false) {
                return this.getAvailabilityMessage(availability)
            }

            const warning = this.getDeclaredWarning(date, hour)
            if (warning) {
                return warning.message
            }

            return 'Aggiungi pianificazione ' + hour + ' - ' + this.addOneHour(hour)
        },

        getAvailability(date, hour) {
            const docenteId = this.selection && this.selection.docente_id ? this.selection.docente_id : null

            if (!docenteId || !this.availability) {
                return null
            }

            const docenteAvailability = this.availability[docenteId] || this.availability[String(docenteId)]

            if (!docenteAvailability) {
                return null
            }

            return docenteAvailability[String(date) + '|' + String(hour)] || null
        },
        getAvailabilityForDocente(docenteId, date, hour) {
            if (!docenteId || !this.availability) return null

            const docenteAvailability = this.availability[docenteId] || this.availability[String(docenteId)]
            return docenteAvailability ? docenteAvailability[String(date) + '|' + String(hour)] || null : null
        },
        getDeclaredWarning(date, hour, selectedSlot) {
            const slot = selectedSlot || this.getCell(date, hour)
            const docenteId = slot && slot.docente_id
                ? slot.docente_id
                : (this.selection && this.selection.docente_id ? this.selection.docente_id : null)
            const availability = this.getAvailabilityForDocente(docenteId, date, hour)
            const declared = availability && availability.declared_availability

            return declared && declared.warning ? declared : null
        },
        getAvailabilityMessage(availability) {
            if (!availability) {
                return 'Docente occupato in questo orario'
            }

            return availability.message ||
                availability.reason ||
                'Docente occupato in questo orario'
        },

        getAvailabilityShortLabel(date, hour) {
            const availability = this.getAvailability(date, hour)

            if (!availability || availability.available !== false) {
                return ''
            }

            const conflict = availability.conflict || {}

            if (conflict.corso_ref) {
                return 'Occupato: ' + conflict.corso_ref
            }

            if (conflict.corso_titolo) {
                return 'Occupato: ' + this.truncateText(conflict.corso_titolo, 24)
            }

            return 'Docente occupato'
        },

        getModuloShort(slot) {
            if (!slot) return ''

            return slot.modulo_short ||
                (slot.modulo && (slot.modulo.modulo || slot.modulo.nome)) ||
                slot.modulo_nome ||
                ''
        },

        getModuloDetail(slot) {
            if (!slot) return ''

            const shortName = String(this.getModuloShort(slot) || '').trim()
            const fullName = String(
                slot.modulo_nome ||
                (slot.modulo && slot.modulo.nome) ||
                ''
            ).trim()

            if (shortName && fullName.indexOf(shortName) === 0) {
                return fullName.substring(shortName.length).replace(/^[\s\-–—:·]+/, '').trim()
            }

            if (!fullName || fullName === shortName) {
                return ''
            }

            return fullName
        },

        getModuloTitle(slot) {
            const shortName = this.getModuloShort(slot)
            const detail = this.getModuloDetail(slot)

            if (shortName && detail) {
                return shortName + ' - ' + detail
            }

            return shortName || detail || ''
        },

        truncateText(text, maxLength) {
            text = String(text || '')

            if (text.length <= maxLength) {
                return text
            }

            return text.substring(0, maxLength - 1) + '…'
        },

        onClick(date, hour) {
            if (this.mode !== 'modifica') return
            if (this.isDateLocked(date, this.activeCourseId)) return
            if (!this.isActivePhase(hour)) return

            const availability = this.getAvailability(date, hour)
            const selectedAulaId = this.selection && this.selection.aula_id ? this.selection.aula_id : 0

            this.$emit('cell-click', {
                date: date,
                hour: hour,
                start: hour,
                end: this.addOneHour(hour),
                aula_id: selectedAulaId,
                occupied: false,
                unavailable: availability && availability.available === false,
                availability: availability,
                slot: null
            })
        },

        onSlotClick(date, hour, slot) {
            if (this.mode !== 'modifica') return
            if (this.isDateLocked(date, slot.corso_id)) return
            this.$emit('cell-click', {
                date: date,
                hour: hour,
                start: hour,
                end: this.addOneHour(hour),
                aula_id: slot.aula_id || 0,
                occupied: true,
                unavailable: false,
                availability: this.getAvailabilityForDocente(slot.docente_id, date, hour),
                slot: slot
            })
        },

        onDayClick(day) {
            if (this.mode !== 'modifica') return
            if (this.isDateLocked(day.date, this.activeCourseId)) return

            this.$emit('day-click', day)
        },

        requestAvailabilityConfirmation(date, hour, selectedSlot) {
            const slot = selectedSlot || this.getCell(date, hour)
            if (!slot) return
            if (this.isDateLocked(date, slot.corso_id)) return
            this.$emit('availability-request', {
                giornata_id: slot.giornata_id || slot.id,
                date: date,
                start: hour,
                end: this.addOneHour(hour),
                slot: slot
            })
        },

        onContextMenu(date, hour, selectedSlot) {
            if (this.mode !== 'modifica') return

            const slot = selectedSlot || this.getCell(date, hour)
            if (!slot) return
            if (this.isDateLocked(date, slot.corso_id)) return

            this.$emit('cell-context', {
                date: date,
                hour: hour,
                start: hour,
                end: this.addOneHour(hour),
                aula_id: slot.aula_id || 0,
                occupied: true,
                slot: slot
            })
        },

        makeSlotKey(date, hour, aulaId) {
            return String(date) + '|' + String(hour) + '|' + String(aulaId || 0)
        },

        addOneHour(time) {
            const parts = String(time).split(':')
            const h = parseInt(parts[0] || '0', 10)
            const m = parseInt(parts[1] || '0', 10)
            const d = new Date()
            d.setHours(h, m, 0, 0)
            d.setHours(d.getHours() + 1)

            return this.padTime(d.getHours()) + ':' + this.padTime(d.getMinutes())
        },

        padTime(value) {
            value = String(value)
            return value.length < 2 ? '0' + value : value
        },

        weekdayLabel(weekday) {
            const labels = {
                1: 'Lunedì',
                2: 'Martedì',
                3: 'Mercoledì',
                4: 'Giovedì',
                5: 'Venerdì',
                6: 'Sabato',
                7: 'Domenica'
            }

            return labels[weekday] || ''
        }
    }
}
</script>


<style scoped>
.calendar-grid-v2 {
    max-height: calc(100vh - 260px);
    overflow: auto;
    position: relative;
    border: 1px solid #e5e7eb;
    background: #fff;
}

.calendar-grid-v2 table {
    background: #fff;
    border-collapse: separate;
    border-spacing: 0;
    margin-bottom: 0;
}

.calendar-grid-v2 thead th {
    position: sticky;
    top: 0;
    z-index: 20;
    background: #f8fafc;
    box-shadow: 0 1px 0 #dee2e6;
}

.calendar-day-col {
    min-width: 130px;
    width: 130px;
    vertical-align: middle;
    background: #fff;
    position: sticky;
    left: 0;
    z-index: 15;
    box-shadow: 1px 0 0 #dee2e6;
}

.calendar-grid-v2 thead .calendar-day-col {
    z-index: 30;
    background: #f8fafc;
}

.calendar-day-col-actionable {
    cursor: pointer;
}

.calendar-day-col-actionable:hover {
    background: #eff6ff;
    box-shadow: inset -2px 0 0 #3b82f6, 1px 0 0 #dee2e6;
}

.day-action-button {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 6px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #ffffff;
    color: #334155;
    font-size: 10px;
    line-height: 1.2;
    font-weight: 700;
}

.day-action-button:hover {
    border-color: #3b82f6;
    color: #1d4ed8;
}

.calendar-hour-col {
    min-width: 145px;
    text-align: center;
    font-weight: 700;
    color: #475569;
    white-space: nowrap;
}

.hour-start {
    font-size: 18px;
    font-weight: 800;
}

.hour-arrow {
    display: inline-block;
    margin: 0 4px;
    font-size: 12px;
    opacity: .65;
}

.hour-end {
    font-size: 12px;
    font-weight: 600;
    opacity: .75;
}

.hour-phase-label {
    display: block;
    margin-top: 3px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    opacity: .65;
}

.calendar-hour-phase-00 {
    border-top: 3px solid #2563eb !important;
}

.calendar-hour-phase-30 {
    border-top: 3px solid #d97706 !important;
}

.calendar-hour-active-phase {
    background: #eff6ff;
}

.calendar-cell {
    min-width: 145px;
    height: 78px;
    vertical-align: top;
    font-size: 12px;
    transition: background-color .15s ease-in-out, box-shadow .15s ease-in-out, color .15s ease-in-out;
    position: relative;
}

.calendar-cell-empty {
    background: #f8f9fa;
    color: #999;
    text-align: center;
    vertical-align: middle;
}

.calendar-cell-empty.cursor-pointer:hover {
    background: #eef6ff;
    box-shadow: inset 0 0 0 2px #93c5fd;
}

.calendar-cell-occupied {
    background: #f8fafc;
    color: #334155;
}

.calendar-cell-occupied.cursor-pointer:hover {
    background: #eef6ff;
    box-shadow: inset 0 0 0 1px #bfdbfe;
}

.calendar-slot-lane:hover .slot-actions,
.calendar-slot-lane:focus-within .slot-actions {
    opacity: 1;
    transform: translateY(0);
}

.calendar-cell-busy,
.calendar-cell-unavailable {
    background: #fff3cd;
}

.calendar-cell-declared-warning {
    box-shadow: inset 0 0 0 2px #f59e0b;
}

.calendar-cell-locked,
.calendar-cell-locked.cursor-pointer:hover {
    background: #eef2f7;
    box-shadow: inset 0 0 0 1px #cbd5e1;
    color: #64748b;
    cursor: not-allowed;
}

.calendar-cell-other-phase.calendar-cell-empty {
    background: repeating-linear-gradient(
        -45deg,
        #f8fafc,
        #f8fafc 8px,
        #f1f5f9 8px,
        #f1f5f9 16px
    );
    color: #94a3b8;
    cursor: default;
}

.declared-availability-warning {
    position: absolute;
    top: 3px;
    right: 4px;
    z-index: 2;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #f59e0b;
    color: #111827;
    font-weight: 900;
    line-height: 18px;
    text-align: center;
}

.slot-action-request {
    background: #f59e0b;
    color: #111827;
}

.calendar-cell-unavailable.cursor-pointer:hover {
    background: #ffe4e6;
    box-shadow: inset 0 0 0 2px #fb7185;
}

.cursor-pointer {
    cursor: pointer;
}

.calendar-empty-content {
    font-size: 22px;
    line-height: 36px;
    opacity: .7;
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.availability-hint {
    display: block;
    font-size: 10px;
    line-height: 1.2;
    color: #92400e;
    margin-top: 2px;
}

.calendar-slot-content {
    position: relative;
    height: 100%;
    padding: 2px 4px 16px 4px;
    overflow: hidden;
}

.calendar-slot-lane {
    height: auto;
    min-height: 78px;
    margin-bottom: 4px;
    padding: 5px 5px 20px 7px;
    border-left: 5px solid #2563eb;
    border-radius: 4px;
    background: #334155;
    color: #fff;
    cursor: pointer;
}

.calendar-slot-lane:last-of-type {
    margin-bottom: 0;
}

.calendar-slot-lane:hover {
    background: #b91c1c;
}

.calendar-slot-locked,
.calendar-slot-locked:hover {
    background: #64748b;
    cursor: not-allowed;
    opacity: .82;
}

.calendar-slot-locked .slot-actions {
    display: none;
}

.slot-course {
    display: block;
    padding-right: 24px;
    margin-bottom: 3px;
    overflow: hidden;
    color: rgba(255, 255, 255, .88);
    font-size: 9px;
    font-weight: 800;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.parallel-add-button {
    width: 100%;
    margin-top: 3px;
    padding: 2px 4px;
    border: 1px dashed #93c5fd;
    border-radius: 4px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 10px;
    font-weight: 700;
}

.parallel-add-button:disabled {
    border-color: #cbd5e1;
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
}

.slot-module {
    line-height: 1.2;
    margin-bottom: 4px;
    display: flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;
}

.slot-module-badge {
    flex: 0 0 auto;
    display: inline-block;
    font-weight: 800;
    font-size: 11px;
    padding: 2px 5px;
    border-radius: 4px;
    background: rgba(255, 255, 255, .18);
    color: #fff;
}

.slot-module-detail {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 9px;
    font-weight: 600;
    opacity: .86;
    color: rgba(255, 255, 255, .94);
}

.slot-teacher {
    font-size: 12px;
    line-height: 1.2;
    margin-top: 3px;
    font-weight: 700;
    color: #ffffff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, .18);
}

.slot-extra {
    font-size: 10px;
    line-height: 1.2;
    margin-top: 2px;
    opacity: .95;
}

.slot-actions {
    position: absolute;
    right: 5px;
    bottom: 4px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transform: translateY(3px);
    transition: opacity .15s ease-in-out, transform .15s ease-in-out;
}

.slot-action-btn {
    border: 0;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 999px;
    background: rgba(255, 255, 255, .22);
    color: #fff;
    line-height: 1.2;
}

.slot-action-btn.slot-action-request {
    background: #f59e0b;
    color: #111827;
}

.course-hours-exception-badge {
    position: absolute;
    top: 3px;
    left: 4px;
    z-index: 2;
    padding: 1px 5px;
    border-radius: 999px;
    background: #7c3aed;
    color: #fff;
    font-size: 9px;
    font-weight: 800;
}

.slot-action-edit {
    background: rgba(255, 255, 255, .92);
    color: #1d4ed8;
}

.slot-action-edit:hover,
.slot-action-edit:focus {
    background: #ffffff;
    color: #1e40af;
}

.slot-action-delete {
    display: inline-block;
}
</style>
